import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// httpOnly cookie auth — every request carries the `wp_auth` cookie set by the
// backend. `withCredentials: true` is required for the browser to send it on
// cross-origin requests (frontend and backend live on the same domain via the
// ingress; the flag is still required because axios wouldn't send cookies
// otherwise).
export const http = axios.create({
  baseURL: API,
  timeout: 180000,
  withCredentials: true,
});

// Sprint 60.46 — Admin impersonation. When an admin uses the "Impersonate"
// action, a short-lived JWT is stashed in localStorage. Every subsequent
// request attaches it as `Authorization: Bearer` so /auth/me + all guarded
// endpoints see the target user. Auto-cleared on expiry. The httpOnly
// `wp_auth` cookie still gets sent but the header takes precedence.
http.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem("wp_impersonation");
    if (raw) {
      const { token, expires_at } = JSON.parse(raw);
      if (expires_at && Date.now() < expires_at && token) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${token}`;
      } else {
        localStorage.removeItem("wp_impersonation");
      }
    }
  } catch (_) { /* localStorage disabled — noop */ }
  return config;
});

// Sprint 60.62 — Cloudflare cold-pod retry interceptor.
//
// After a production redeploy, the first request to a freshly-started K8s
// pod occasionally races the app's Mongo warm-up and Cloudflare receives
// a truncated / malformed body from the origin → surfaces its own 520
// ("origin sent a response Cloudflare could not parse") to the browser.
// The pod stabilizes within 3-5 seconds, so a single silent retry with a
// small backoff clears the failure without any user action.
//
// We only retry when:
//   * Response is a Cloudflare edge 5xx (520-524) OR a hard network error
//     (no `error.response` at all — connection reset / DNS blip / etc.).
//   * The request is safe to replay: every GET is idempotent by definition,
//     and specific POSTs are marked idempotent via `config._idempotent`.
//     `/api/optimize` returns 409 on duplicate submission so it's safe.
//   * We haven't already retried this exact request (guard flag on config).
//
// Anything else (4xx auth failures, business-logic 5xx from our own app,
// user-triggered POSTs without the idempotency marker) bubbles up unchanged.
const CLOUDFLARE_EDGE_STATUS = new Set([520, 521, 522, 523, 524]);
const IDEMPOTENT_POST_PATHS = new Set([
  "/optimize",
  "/optimize/fill-gap",
  "/optimize/stop",
  "/data/refresh",
  "/ai/analyze",
  "/ai/consensus-advisor",
]);

http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const config = error?.config;
    if (!config || config._wpRetried) return Promise.reject(error);

    const method = (config.method || "get").toLowerCase();
    const path = (config.url || "").split("?")[0];
    const status = error?.response?.status;
    const isEdgeError = CLOUDFLARE_EDGE_STATUS.has(status);
    const isNetworkError = !error?.response;  // no response at all

    // Only replay safe requests. GETs always safe; specific POSTs opt-in.
    const isSafe =
      method === "get" ||
      method === "head" ||
      (method === "post" && IDEMPOTENT_POST_PATHS.has(path));

    if (!isSafe || (!isEdgeError && !isNetworkError)) {
      return Promise.reject(error);
    }

    config._wpRetried = true;
    const delay = isEdgeError ? 3000 : 1500;
    console.debug(
      `[wp] retry after ${delay}ms — ${method.toUpperCase()} ${path} (` +
      `${isEdgeError ? `CF ${status}` : "network error"})`
    );
    await new Promise((res) => setTimeout(res, delay));
    return http.request(config);
  },
);

export const api = {
  summary: () => http.get("/state/summary").then((r) => r.data),
  dkContest: (id) => http.get(`/dk/contest/${id}`).then((r) => r.data),
  uploadDK: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/dk/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  uploadCompetitor: (source, file, archetype = null) => {
    const fd = new FormData();
    fd.append("file", file);
    const q = new URLSearchParams({ source });
    if (archetype) q.set("archetype", archetype);
    return http.post(`/competitors/upload?${q.toString()}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  deleteCompetitor: (source) => http.delete(`/competitors/${encodeURIComponent(source)}`).then((r) => r.data),
  getPool: (limit = 1200) => http.get(`/dk/pool?limit=${limit}`).then((r) => r.data),
  poolExportURL: () => `${API}/dk/pool/export`,
  getConsensus: (limit = 500) => http.get(`/consensus?limit=${limit}`).then((r) => r.data),
  optimize: (cfg) => http.post("/optimize", cfg).then((r) => r.data),
  optimizeProgress: () => http.get("/optimize/progress").then((r) => r.data),
  optimizeStop: (force = false) => http.post(`/optimize/stop${force ? "?force=true" : ""}`).then((r) => r.data),
  optimizeFillGap: (target, base_config) =>
    http.post("/optimize/fill-gap", { target, base_config: base_config || {} })
        .then((r) => r.data),
  getLineups: () => http.get("/lineups").then((r) => r.data),
  getCandidates: () => http.get("/candidates").then((r) => r.data),
  promoteCandidate: (body) => http.post("/candidates/promote", body).then((r) => r.data),
  undoPromote: () => http.post("/candidates/undo").then((r) => r.data),
  resultsLatest: () => http.get("/results/latest").then((r) => r.data),
  rulesetPreview: (params) => http.get("/optimize/ruleset-preview", { params }).then((r) => r.data),
  sundayStatus: (source = "smart") => http.get("/optimize/sunday-status", { params: { source } }).then((r) => r.data),
  fixInactive: (source = "smart") => http.post("/optimize/fix-inactive", null, { params: { source } }).then((r) => r.data),
  fixLineup: (lineup_id, drop_dk_ids = [], mode = "swap") =>
    http.post("/optimize/fix-lineup", { lineup_id, drop_dk_ids, mode }).then((r) => r.data),
  getRawLineups: () => http.get("/optimize/raw/lineups").then((r) => r.data),
  clearLineups: () => http.delete("/lineups").then((r) => r.data),
  clearRawLineups: () => http.delete("/optimize/raw/lineups").then((r) => r.data),
  exportURL: () => `${API}/lineups/export`,
  aiAnalyze: (focus = "leverage") => http.post("/ai/analyze", { focus }).then((r) => r.data),
  aiLast: () => http.get("/ai/last").then((r) => r.data),
  aiConsensusAdvisor: () => http.post("/ai/consensus-advisor").then((r) => r.data),
  aiConsensusAdvisorLast: () => http.get("/ai/consensus-advisor/last").then((r) => r.data),
  aiChalkWatch: (limit = 5) => http.get(`/ai/chalk-watch?limit=${limit}`).then((r) => r.data),
  slateChalkPivots: (chalk_floor = 15, pivot_ceiling = 5) =>
    http.get(`/ai/slate-chalk-pivots?chalk_floor=${chalk_floor}&pivot_ceiling=${pivot_ceiling}`)
      .then((r) => r.data),
  aiPivotSuggestions: (dkId, limit = 5) =>
    http.get(`/ai/pivot-suggestions?dk_id=${encodeURIComponent(dkId)}&limit=${limit}`)
      .then((r) => r.data),
  reset: () => http.post("/reset").then((r) => r.data),
  // Data pipeline
  dataRefresh: () => http.post("/data/refresh").then((r) => r.data),
  refreshPoolProjections: () => http.post("/pool/refresh-projections").then((r) => r.data),
  dataSnapshot: () => http.get("/data/snapshot").then((r) => r.data),
  dataScheduler: () => http.get("/data/scheduler").then((r) => r.data),
  // DK slate auto-ingest (no CSV upload required)
  dkSlatesList: () => http.get("/dk/slates").then((r) => r.data),
  dkSlates: () => http.get("/dk/slates").then((r) => r.data),
  dkSlatesRefresh: () => http.post("/dk/slates/refresh-now").then((r) => r.data),
  dkSlateApply: (dg, opts = {}) => http.post(`/dk/slates/${dg}/apply`, undefined, opts).then((r) => r.data),
  dkSlateAutoDetect: () => http.post("/dk/slates/auto-detect-apply").then((r) => r.data),
  hubRooms: () => http.get("/hub/rooms").then((r) => r.data),
  hubMessages: (room, since) => http.get("/hub/messages", { params: { room, since } }).then((r) => r.data),
  hubPost: (body) => http.post("/hub/messages", body).then((r) => r.data),
  hubDelete: (id) => http.delete(`/hub/messages/${id}`).then((r) => r.data),
  hubReport: (id) => http.post(`/hub/messages/${id}/report`).then((r) => r.data),
  hubMutes: () => http.get("/hub/mutes").then((r) => r.data),
  hubMute: (userId) => http.post("/hub/mutes", { user_id: userId }).then((r) => r.data),
  hubUnmute: (userId) => http.delete(`/hub/mutes/${userId}`).then((r) => r.data),
  hubNews: (kind) => http.get("/hub/news", { params: { kind } }).then((r) => r.data),
  hubReact: (id, kind) => http.post(`/hub/news/${id}/react`, { kind }).then((r) => r.data),
  hubSentiment: () => http.get("/hub/sentiment").then((r) => r.data),
  hubRules: () => http.get("/hub/rules").then((r) => r.data),
  hubRebuildAffected: (dkId, source = "smart") =>
    http.post(`/optimize/fix-inactive?source=${source}&dk_id=${encodeURIComponent(dkId)}`).then((r) => r.data),
  hubAdminReports: () => http.get("/hub/admin/reports").then((r) => r.data),
  hubAdminBan: (userId, reason) => http.post("/hub/admin/ban", { user_id: userId, reason }).then((r) => r.data),
  hubAdminUnban: (userId) => http.delete(`/hub/admin/ban/${userId}`).then((r) => r.data),
  ownCalUpload: (csvText, contestLabel) =>
    http.post("/calibration/ownership", { csv_text: csvText, contest_label: contestLabel }).then((r) => r.data),
  ownCalHistory: () => http.get("/calibration/ownership/history").then((r) => r.data),
  ownCalDelete: (id) => http.delete(`/calibration/ownership/${id}`).then((r) => r.data),
  // Cross-device UI preferences (Uploads slate-filter chip, etc.)
  getPrefs: () => http.get("/prefs").then((r) => r.data),
  updatePrefs: (prefs) => http.patch("/prefs", { prefs }).then((r) => r.data),
  lineupsRangeOfOutcomes: (nSims = 25000) =>
    http.get(`/lineups/range-of-outcomes?n_sims=${nSims}`).then((r) => r.data),
  // Late injury
  suggestBeneficiary: (dkId) => http.get(`/injury/suggest-beneficiary/${encodeURIComponent(dkId)}`).then((r) => r.data),
  lateOut: (ruledOutId, beneficiaryId) => http.post("/injury/late-out", { ruled_out_dk_id: ruledOutId, beneficiary_dk_id: beneficiaryId || null }).then((r) => r.data),
  clearLateOut: (dkId) => http.delete(`/injury/late-out/${encodeURIComponent(dkId)}`).then((r) => r.data),
  // Snap Surge
  snapSurgeRefresh: () => http.post("/snap-surge/refresh").then((r) => r.data),
  snapSurgeGet: () => http.get("/snap-surge").then((r) => r.data),
  // Contest Sizing
  contestPresets: () => http.get("/contest/presets").then((r) => r.data),
  contestSizing: (payload) => http.post("/contest/sizing", payload).then((r) => r.data),
  // Late Swap
  lateSwapGames: () => http.get("/late-swap/late-games").then((r) => r.data),
  lateSwapAnalyze: (payload) => http.post("/late-swap/analyze", payload).then((r) => r.data),
  // Framework defaults (proprietary rulebook endpoint removed Sprint 31)
  frameworkDefaults: () => http.get("/framework/defaults").then((r) => r.data),
  // Compliance
  complianceRule: () => http.get("/compliance/rule").then((r) => r.data),
  // Injury / late-swap cascade upgrade
  injuryUpgradeCascade: (body) => http.post("/injury/upgrade-cascade", body).then((r) => r.data),
  // Fingerprint Variance™ (per-user session salt)
  sessionSalt: () => http.get("/session/salt").then((r) => r.data),
  sessionRotateSalt: () => http.post("/session/rotate-salt").then((r) => r.data),
  // Slate + Weather
  slateInfo: () => http.get("/slate/info").then((r) => r.data),
  slateGames: () => http.get("/slate/games").then((r) => r.data),
  healthDiag: () => http.get("/health/diag").then((r) => r.data),
  providersHealth: () => http.get("/admin/providers/health").then((r) => r.data),
  slateWeather: () => http.get("/slate/weather").then((r) => r.data),
  slateGamePivots: () => http.get("/slate/game-pivots").then((r) => r.data),
  slatePivotBoard: (perPosition = 4) => http.get(`/slate/pivot-board?per_position=${perPosition}`).then((r) => r.data),
  // Portfolio Diversity Score
  lineupsDiversity: () => http.get("/lineups/diversity").then((r) => r.data),
  // Slate Attack Plan (AI) — optional preset key (small_gpp / milly_maker / etc.)
  aiAttackPlan: (preset, customParams) => {
    const body = {};
    if (preset) body.preset = preset;
    if (customParams) body.custom_params = customParams;
    return http.post("/ai/attack-plan", body).then((r) => r.data);
  },
  aiAttackPlanLast: () => http.get("/ai/attack-plan/last").then((r) => r.data).catch(() => null),
  // Portfolio Split — generate multiple batches at once
  optimizeSplit: (payload) => http.post("/optimize/split", payload).then((r) => r.data),
  splitBatches: () => http.get("/lineups/split").then((r) => r.data),
  splitBatch: (preset) => http.get(`/lineups/split/${preset}`).then((r) => r.data),
  splitBatchExportUrl: (preset) => `${API}/lineups/split/${preset}/export`,
  // Contest Simulator
  contestSimulate: (payload) => http.post("/contest/simulate", payload).then((r) => r.data),
  contestSimulateLast: (preset) => http.get(`/contest/simulate/last${preset ? `?preset=${preset}` : ''}`).then((r) => r.data),
  // Injury events (real-time push)
  injuryEvents: (since, onlySlate = true) => {
    const params = new URLSearchParams();
    if (since) params.set("since", since);
    if (onlySlate) params.set("only_slate", "true");
    return http.get(`/injuries/events?${params}`).then((r) => r.data);
  },
  pollInjuriesNow: () => http.post("/injuries/events/poll-now").then((r) => r.data),
  // Injury Feed
  injuries: () => http.get("/injuries").then((r) => r.data),
  injuriesSlate: () => http.get("/injuries/slate").then((r) => r.data),
  // DK Entry Template (Sprint 28) — multi-contest one-click push
  entryTemplateGet: () => http.get("/dk/entry-template").then((r) => r.data),
  entryTemplateUpload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/dk/entry-template", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  entryTemplateOptimize: (payload) =>
    http.post("/dk/entry-template/optimize", payload).then((r) => r.data),
  entryTemplateDelete: () => http.delete("/dk/entry-template").then((r) => r.data),
  entryTemplateRouting: (source = "smart") =>
    http.get("/dk/entry-template/routing", { params: { source } }).then((r) => r.data),
  entryTemplateRoutingConfirm: (source, allocations) =>
    http.post("/dk/entry-template/routing/confirm", { source, allocations }).then((r) => r.data),
  entryTemplateExportUrl: () => `${API}/dk/entry-template/export`,
  // Sprint 102 — DK upload cart: assign a build to one tournament at a time
  uploadCart: () => http.get("/dk/upload-cart").then((r) => r.data),
  uploadCartAssign: (source, contestId, count) =>
    http.post("/dk/upload-cart/assign", { source, contest_id: contestId, count }).then((r) => r.data),
  uploadCartRemove: (contestId) =>
    http.delete(`/dk/upload-cart/${contestId}`).then((r) => r.data),
  uploadCartClear: () => http.delete("/dk/upload-cart").then((r) => r.data),
  uploadCartExportUrl: () => `${API}/dk/upload-cart/export`,
  // Sprint 87 — auto-sense + multi-tuning build
  shadowLineupsUpload: (source, file, dg) => {
    const fd = new FormData();
    fd.append("file", file);
    const q = new URLSearchParams({ source });
    if (dg != null) q.set("dg", String(dg));
    return http.post(`/shadow/lineups?${q.toString()}`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  shadowLineupsList: () => http.get("/shadow/lineups").then((r) => r.data),
  shadowEmulate: (source, nLineups = 150, archetype = null, force = false) => {
    const q = new URLSearchParams({ source, n_lineups: String(nLineups) });
    if (archetype) q.set("archetype", archetype);
    if (force) q.set("force", "true");
    return http.post(`/shadow/emulate?${q.toString()}`).then((r) => r.data);
  },
  fieldChecklist: () => http.get("/competitors/checklist").then((r) => r.data),
  shadowLineupsDelete: (source) => http.delete(`/shadow/lineups/${source}`).then((r) => r.data),
  entryTemplateAutoPlan: () => http.get("/dk/entry-template/auto-plan").then((r) => r.data),
  entryTemplateAutoBuild: (payload) =>
    http.post("/dk/entry-template/auto-build", payload).then((r) => r.data),
  entryTemplateAutoStatus: () =>
    http.get("/dk/entry-template/auto-build/status").then((r) => r.data),
  entryTemplateAutoReset: () =>
    http.delete("/dk/entry-template/auto-build").then((r) => r.data),
  entryTemplateLineups: () =>
    http.get("/dk/entry-template/lineups").then((r) => r.data),
  contestSimulateAutosense: (payload) =>
    http.post("/contest/simulate/autosense", payload || {}).then((r) => r.data),
  entryTemplateReceipt: () => http.get("/dk/entry-template/receipt").then((r) => r.data),
  // Sprint 91 — rule-by-rule audit of the built portfolio
  portfolioAudit: () => http.get("/portfolio/audit").then((r) => r.data),
  // Sprint 60.77 — optimizer-driven fill (uses lineups already built in
  // Smart or Raw Optimizer instead of re-optimizing off the template).
  entryTemplateFillUrl: (source) => `${API}/dk/entry-template/fill?source=${source}`,
  // Chrome extension distribution
  extensionDownloadUrl: () => `${API}/extension/download`,
  extensionVersion: () => http.get("/extension/version").then((r) => r.data),
};
